import { useMemo } from 'react';

import { usePriceList } from '@zenbusiness/application-commons-pricelist';

import {
  shouldEnableCoupons,
  meetsCouponFlagRequirement,
} from '#client/pages/ProductLanding/components/HeroSection/components/BuyBox/coupons/utils';

import { useServiceFlags } from './useServiceFlags';

/**
 * Determines enabled coupon and prepares structured checkout data for a product.
 *
 * @param {object} params
 * @param {object} params.entitlements - Entitlements map with `has(key)` method
 * @param {string} params.serviceCode - Product service code
 * @param {string} [params.interval] - Pricing interval (defaults to annual)
 *
 * @returns {{
 *   enabledCoupon: object | null,
 *   serviceToAddToCart: object | null
 * }} enabledCoupon: First available coupon or null.
 *     serviceToAddToCart: Checkout data with serviceName, servicePricingUuid, couponUuid, interval.
 */
export const useProductCoupon = ({ entitlements, serviceCode, interval }) => {
  const { get } = usePriceList();
  const flagValues = useServiceFlags(serviceCode);

  const result = useMemo(() => {
    if (!serviceCode || !shouldEnableCoupons(entitlements, serviceCode)) {
      return { enabledCoupon: null, serviceToAddToCart: null };
    }

    const product = get(serviceCode, interval ? { interval } : { interval: 'annual' });
    const coupons = product?.pricing?.coupons || [];
    const servicePricingUuid = product?.pricing?.servicePricingUuid || null;

    const enabledCoupon = coupons[0] || null;

    const shouldApplyCoupon = meetsCouponFlagRequirement(serviceCode, enabledCoupon, flagValues);

    const serviceToAddToCart = servicePricingUuid
      ? {
          serviceName: serviceCode,
          servicePricingUuid,
          couponUuid: shouldApplyCoupon ? enabledCoupon?.uuid : null,
          interval: interval || 'annual',
        }
      : null;

    return { enabledCoupon, serviceToAddToCart };
  }, [entitlements, serviceCode, interval, get, flagValues]);

  return result;
};

export default useProductCoupon;
